import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone, Mail } from "lucide-react";

const WHATSAPP_NUMBER = "919477856303";
const EMAIL = "communications@operexagennex.com";

export default function FloatingChat() {
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="floating-chat" className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="bg-[#0a0a0c] border border-white/10 backdrop-blur-xl shadow-2xl w-72 overflow-hidden"
          >
            <div className="bg-[#050505] px-5 py-4 border-b border-white/10">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-1">Get in Touch</div>
              <div className="font-display text-lg text-white font-semibold leading-tight">How can we help?</div>
              <div className="text-xs text-zinc-500 mt-1">We typically respond within one business day.</div>
            </div>
            <div className="p-3 space-y-2">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello Operexa Gennex, I'd like to discuss a project.")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-whatsapp"
                className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors group"
              >
                <div className="w-10 h-10 bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#25D366]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white group-hover:text-[#25D366] transition-colors">Live Chat (WhatsApp)</div>
                  <div className="text-xs text-zinc-500">+91 94778 56303</div>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL}?subject=${encodeURIComponent("Inquiry — Operexa Gennex")}`}
                data-testid="floating-email"
                className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors group"
              >
                <div className="w-10 h-10 bg-[#3B82F6]/15 border border-[#3B82F6]/40 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#3B82F6]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white group-hover:text-[#3B82F6] transition-colors">Email Us</div>
                  <div className="text-xs text-zinc-500 truncate">{EMAIL}</div>
                </div>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen(!open)}
        data-testid="floating-toggle"
        aria-label={open ? "Close chat menu" : "Open chat menu"}
        className="relative w-14 h-14 rounded-full bg-[#3B82F6] hover:bg-[#60A5FA] text-white shadow-lg shadow-[#3B82F6]/30 flex items-center justify-center transition-colors"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute"
            >
              <X className="w-6 h-6" />
            </motion.span>
          ) : (
            <motion.span
              key="msg"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute"
            >
              <MessageCircle className="w-6 h-6" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute inset-0 rounded-full border border-[#3B82F6]/40 animate-ping pointer-events-none" />
        )}
      </button>
    </div>
  );
}
